import { Product as Entity } from './product.model';
import { AppState } from '../app-store.state';
import { Injectable } from '@angular/core';
import {select, Store} from '@ngrx/store';
import {Observable, Subject} from 'rxjs';
import {IPage, ITableSort} from "../app-store.interfaces";
import {isCurrentEntity, ProductSelectors as Selectors} from "./product.selectors";
import {ProductActions as Actions} from './product.actions';
import {Criteria} from "../app-store.types";
import {generateUniqueId} from "../app-store.functions";
import {PaginatedResult} from "../app-store-entity.service";
import {filter, first, map} from "rxjs/operators";

@Injectable({ providedIn: 'root' })
export class ProductFacade {
  all$: Observable<Entity[]> = this.store.pipe(select(Selectors.selectAll));
  isLoading$: Observable<boolean> = this.store.pipe(select(Selectors.isLoading));
  isSaving$: Observable<boolean> = this.store.pipe(select(Selectors.isSaving));
  hasError$: Observable<Error | undefined> = this.store.pipe(select(Selectors.hasErrors));
  current$: Observable<Entity | undefined> = this.store.pipe(select(Selectors.currentEntity));
  currentId$: Observable<number | undefined> = this.store.pipe(select(Selectors.currentEntityId));
  currentEntities$: Observable<Entity[] | undefined> = this.store.pipe(select(Selectors.currentEntities));
  currentEntitiesCount$: Observable<number> = this.store.pipe(select(Selectors.currentEntities),map(x => x?.length ?? 0));
  currentIds$: Observable<number[] | undefined> = this.store.pipe(select(Selectors.currentEntityIds));
  totalPageable$: Observable<number> = this.store.pipe(select(Selectors.totalPageable));
  currentPage$: Observable<IPage> = this.store.pipe(select(Selectors.currentPage));
  currentSort$: Observable<ITableSort | undefined> = this.store.pipe(select(Selectors.currentSort));
  currentColours$: Observable< string[] | undefined> = this.store.pipe(select(Selectors.currentColours));
  currentExternalCategories$: Observable< string[] | undefined> = this.store.pipe(select(Selectors.currentExternalCategories));

  private _refreshPage = new Subject<void>();
  refreshPage$: Observable<void> = this._refreshPage.asObservable();

  constructor(
    private store: Store<AppState>,
  ) {

  }

  loadAndSelectIfNecessary(productId: number): void {
    this.store.dispatch(Actions.loadAndSelectIfNecessary({productId}));
  }

  refreshPage(): void {
    this._refreshPage.next();
  }

  isCurrentEntity(entityId: number): Observable<boolean> {
    return this.store.select(isCurrentEntity(entityId));
  }

  setPaginationSettings(page: IPage): void {
    this.store.dispatch(Actions.setPaginationSettings({page}));
    this.refreshPage();
  }

  setSortSettings(tableSort: ITableSort): void {
    this.store.dispatch(Actions.setSortSettings({tableSort}));
  }

  setCurrentId(entityId: number): void {
    this.store.dispatch(Actions.setCurrentEntityId({entityId}));
  }

  selectEntities(entities: Entity[]): void {
    this.store.dispatch(Actions.selectEntities({entities}));
  }
  deselectEntities(entities: Entity[]): void {
    this.store.dispatch(Actions.deselectEntities({entities}));
  }

  deselectAll(): void {
    this.store.dispatch(Actions.deselectAll());
  }

  loadPage(pageNumber: number, pageSize: number, criteria: Criteria = undefined): void {
    this.store.dispatch(Actions.loadPage({criteria, page: {pageNumber, pageSize}}));
  }

  loadPageWithResult(pageNumber: number, pageSize: number, criteria: Criteria = undefined): Observable<PaginatedResult<Entity>> {
    const correlationId = generateUniqueId();
    this.store.dispatch(Actions.loadPage({page: {pageNumber, pageSize}, criteria, correlationId: correlationId}));
    return this.store.select(Selectors.findByCorrelationId<PaginatedResult<Entity>>(correlationId))
      .pipe(filter((x) => !!x));
  }

  load(criteria: Criteria = undefined): void {
    this.store.dispatch(Actions.load({criteria}));
  }

  loadWithResult(criteria: Criteria = undefined): Observable<Entity[]> {
    const correlationId = generateUniqueId();
    this.store.dispatch(Actions.load({criteria, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity[]>(correlationId))
      .pipe(filter((x) => !!x));
  }

  loadSingleWithResult(entityId: number): Observable<Entity> {
    const correlationId = generateUniqueId();
    this.store.dispatch(Actions.loadSingleIfNecessary({entityId, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity>(correlationId))
      .pipe(filter((x) => !!x), first());
  }

  // Force load a single entity from API (always fetches, ignores cache)
  loadSingle(entityId: number): void {
    this.store.dispatch(Actions.loadSingle({entityId}));
  }

  // Force load a single entity from API and return result
  loadSingleForceWithResult(entityId: number): Observable<Entity> {
    const correlationId = generateUniqueId();
    this.store.dispatch(Actions.loadSingle({entityId, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity>(correlationId))
      .pipe(filter((x) => !!x), first());
  }

  addToStore(entities: Entity[]): void {
    this.store.dispatch(Actions.addToStore({entities}));
  }

  create(entity: Entity): void {
    this.store.dispatch(Actions.create({entity}));
  }

  createWithResult(entity: Entity): Observable<Entity> {
    const correlationId = generateUniqueId();

    this.store.dispatch(Actions.create({entity, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity>(correlationId))
      .pipe(filter((x) => !!x));
  }

  createMany(entities: Entity[]): void {
    this.store.dispatch(Actions.createMany({entities}));
  }

  createManyWithResult(entities: Entity[]): Observable<Entity[]> {
    const correlationId = generateUniqueId();

    this.store.dispatch(Actions.createMany({entities, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity[]>(correlationId))
      .pipe(filter((x) => !!x));
  }

  update(entity: Entity): void {
    this.store.dispatch(Actions.update({entity, correlationId: generateUniqueId()}));
  }

  updateWithResult(entity: Entity): Observable<Entity> {
    const correlationId = generateUniqueId();

    this.store.dispatch(Actions.update({entity, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity>(correlationId))
      .pipe(filter((x) => !!x));
  }

  remove(entityId: number): void {
    this.store.dispatch(Actions.remove({entityId}));
  }

  removeWithResult(entityId: number): Observable<Entity> {
    const correlationId = generateUniqueId();

    this.store.dispatch(Actions.remove({entityId, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity>(correlationId))
      .pipe(filter((x) => !!x));
  }

  clone(entity: Entity): void {
    this.store.dispatch(Actions.clone({entity}));
  }

  cloneWithResult(entity: Entity): Observable<Entity> {
    const correlationId = generateUniqueId();

    this.store.dispatch(Actions.clone({entity, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity>(correlationId))
      .pipe(filter((x) => !!x));
  }

  getById(entityId: number): Observable<Entity | undefined> {
    return this.store.pipe(select(Selectors.selectById(entityId)));
  }

  getTotalCount(): Observable<number> {
    return this.store.select(Selectors.totalCount);
  }

  setTotalCount(): void {
    this.store.dispatch(Actions.getTotalProductCount());
  }

  getProductColours() {
    this.store.dispatch(Actions.getTotalProductColours());
  }

  getProductExternalCategories() {
    this.store.dispatch(Actions.getProductExternalCategories());
  }

  formatNameColour(product: Entity): string {
    const name = (product.name ?? '').trim();
    const colour = (product.colour ?? '').trim();
    return name && colour ? `${name} - ${colour}` : (name || colour || '');
  }

  formatProductLabel(product: Entity, includeExternalKey: boolean = false): string {
    const nameColour = this.formatNameColour(product);
    if (!includeExternalKey) return nameColour;

    const ek = (product.externalKey ?? '').trim();
    if (ek && nameColour) return `${ek} (${nameColour})`;
    return ek || nameColour;
  }
}
